import {Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, AfterViewInit, ChangeDetectionStrategy} from '@angular/core';
import {Option, OptionGroupMultiSelect} from '../../../shared/models/option';
import {CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition} from '@angular/cdk/overlay';
import {Observable, Subscription} from 'rxjs';
import {ClickService} from '../../../core/services/click.service';
import {SearchService} from '../../../core/services/search.service';
import {SpinnerComponent} from '../spinner/spinner.component';
import {CdkFixedSizeVirtualScroll, CdkVirtualForOf, CdkVirtualScrollViewport} from '@angular/cdk/scrolling';
import {RouterLink} from '@angular/router';
import {NgClass} from '@angular/common';
import {PillComponent} from '../pill/pill.component';
import {FormsModule} from '@angular/forms';

@Component({
  selector: 'motn-popover-multi-select',
  templateUrl: './popover-multi-select.component.html',
  styleUrls: ['./popover-multi-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CdkConnectedOverlay,
    SpinnerComponent,
    CdkVirtualScrollViewport,
    RouterLink,
    NgClass,
    PillComponent,
    FormsModule,
    CdkOverlayOrigin,
    CdkFixedSizeVirtualScroll,
    CdkVirtualForOf,
  ],
})
export class PopoverMultiSelectComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  private static readonly ITEM_HEIGHT = 40;

  @Input() multiOrSingle: 'multi' | 'single' = 'multi';
  @Input() inputPlaceholder: string = '';
  @Input() autoFocusSearch: boolean = true;
  /**
   * Required if optionGroup$ is not supplied
   */
  @Input() optionGroup: OptionGroupMultiSelect;
  /**
   * Optional. Observable that will be subscribed to on initialization,
   * whose emitted values will be stored to this.optionGroup
   */
  @Input() optionGroup$: Observable<OptionGroupMultiSelect>;
  @Input() overlayPositions: ConnectedPosition[] = [{
    originX: 'start',
    originY: 'top',
    overlayX: 'end',
    overlayY: 'top',
  }];
  /** Anchor the overlay to an element outside this component */
  @Input() externalOrigin: CdkOverlayOrigin | null = null;
  /** Shows a "Create X" row when search has no match. null = hidden */
  @Input() createLabel: string | null = null;
  /** Dims the create row without hiding it */
  @Input() createDisabled = false;
  /** When true, automatically disables the create button while search input is empty */
  @Input() createRequiresInput = false;
  /** Show spinner instead of empty state while data is loading */
  @Input() loading = false;
  /** Capitalize each word as the user types in the search box */
  @Input() capitalizeInput = false;
  /** Clicks originating from this element won't trigger clickedOutsideMenu$ */
  @Input() triggerEl: HTMLElement | null = null;

  searchTerm: string = '';
  options: Option[] = [];
  shownOptions: Option[] = [];

  @Output() ready$: EventEmitter<OptionGroupMultiSelect> = new EventEmitter<OptionGroupMultiSelect>();
  @Output() clickedOption$: EventEmitter<Option> = new EventEmitter<Option>();
  @Output() clickedOutsideMenu$: EventEmitter<null> = new EventEmitter<null>();
  @Output() clickedAnywhere$: EventEmitter<Option[]> = new EventEmitter<Option[]>();
  @Output() createClicked$ = new EventEmitter<string>();
  @Output() searchTermChange = new EventEmitter<string>();

  private clickSub: Subscription;
  private optionGroupSub: Subscription;

  @ViewChild('menuOverlay', {read: ElementRef}) private menuEl: ElementRef<HTMLElement>;
  @ViewChild('searchBox', {read: ElementRef}) private searchEl: ElementRef<HTMLInputElement>;
  @ViewChild(CdkVirtualScrollViewport) private viewport: CdkVirtualScrollViewport;
  @ViewChild('internalOrigin', {static: true}) internalOrigin: CdkOverlayOrigin;

  get activeOrigin(): CdkOverlayOrigin {
    return this.externalOrigin ?? this.internalOrigin;
  }


  constructor(
    protected eRef: ElementRef,
    protected clickService: ClickService,
    protected searchService: SearchService,
  ) {
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (changes['optionGroup'] && !changes['optionGroup'].firstChange && !this.optionGroup$) {
      this.updateOptions();
    }
  }


  ngOnInit(): void {
    this.clickSub = this.clickService.click$.subscribe(
      ($el) => {
        const insideHost = this.eRef.nativeElement.contains($el);
        const insideOverlay = this.menuEl?.nativeElement?.contains($el);
        const insideTrigger = this.triggerEl?.contains($el) ?? false;
        if (!insideHost && !insideOverlay && !insideTrigger) {
          this.clickedOutsideMenu$.emit(null);
          this.clickedAnywhere$.emit(this.optionGroup.getSelected());
        }
      },
    );

    if (this.optionGroup$) {
      this.optionGroupSub = this.optionGroup$.subscribe(
        (optionGroup) => {
          this.optionGroup = optionGroup;
          this.render();
        },
      );
    } else {
      this.render();
    }
  }


  ngOnDestroy() {
    this.clickSub?.unsubscribe();
    this.optionGroupSub?.unsubscribe();
  }


  ngAfterViewInit() {
    // Scroll to selected item once the view is initialized
    // Wait for change detection to complete
    setTimeout(() => {
      if (this.viewport) {
        this.scrollToSelectedItem();
      }
    }, 0);
  }


  private render() {
    this.ready$.emit(this.optionGroup);
    this.updateOptions();

    if (this.autoFocusSearch) {
      setTimeout(() => this.searchEl?.nativeElement.focus(), 0);
    }
  }

  private updateOptions() {
    if (!this.optionGroup || !this.optionGroup.options) {
      return;
    }

    this.options = [...this.optionGroup.options].sort((a, b) => a.name < b.name ? -1 : a.name > b.name ? 1 : 0);
    this.determineShownOptions();
  }


  private scrollToSelectedItem() {
    const selected = this.optionGroup?.getSelected()?.[0];
    if (!selected) {
      return;
    }

    const selectedIndex = this.shownOptions.findIndex(opt => opt.id === selected.id);
    if (selectedIndex < 0) {
      return;
    }

    if (!this.viewport) {
      return;
    }

    const viewportHeight = this.viewport.getViewportSize();
    const itemHeight = PopoverMultiSelectComponent.ITEM_HEIGHT;

    // Center the selected item in the viewport
    const offset = (selectedIndex * itemHeight) - (viewportHeight / 2) + (itemHeight / 2);

    // Scroll to center the selected item, ensuring we don't scroll below 0
    this.viewport.scrollToOffset(Math.max(0, offset), 'auto');
  }


  trackByOptionId(_index: number, option: Option): string | number {
    return option.id;
  }

  determineShownOptions() {
    if (!this.searchTerm) {
      this.shownOptions = this.options;
    } else {
      this.shownOptions = this.searchService.searchOptions(this.searchTerm, this.options);
    }
  }

  onSearchInput() {
    if (this.capitalizeInput) {
      this.searchTerm = this.searchTerm.toUpperCase();
    }
    this.determineShownOptions();
    this.searchTermChange.emit(this.searchTerm);
  }

  onEnterKey(): void {
    const disabled = this.createDisabled || (this.createRequiresInput && !this.searchTerm.trim());
    if (this.createLabel && !disabled && this.searchTerm.trim()) {
      this.createClicked$.emit(this.searchTerm.trim());
    }
  }


  onClick(option: Option) {
    if (option.callback) {
      option.callback();
    }

    if (this.multiOrSingle === 'single') {
      this.optionGroup.deselectAll();
    }

    option.selected = !option.selected;

    this.clickedOption$.emit(option);
    this.clickedAnywhere$.emit(this.optionGroup.getSelected());
  }

}
